
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

export function OrganizationSignup() {
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    tier: "professional" as "professional" | "enterprise",
    subdomain: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const generateSlug = (name: string) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const handleNameChange = (name: string) => {
    const slug = generateSlug(name);
    setFormData(prev => ({
      ...prev,
      name,
      slug,
      subdomain: slug
    }));
  };

  const getDefaultSeats = (tier: string) => {
    switch (tier) {
      case 'professional':
        return 5; // Team plan defaults to 5 seats
      case 'enterprise':
        return 100; // Enterprise plan defaults to 100 seats
      default:
        return 5;
    }
  };

  const checkSlugAvailability = async (slug: string) => {
    const { data, error } = await supabase
      .from('organizations')
      .select('id')
      .eq('slug', slug)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') {
      console.error('Error checking slug availability:', error);
      return false;
    }

    return !data; // Returns true if slug is available (no data found)
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("Not authenticated");
      }

      console.log('Creating organization with user:', user.id);

      // Check if slug is available
      const isSlugAvailable = await checkSlugAvailability(formData.slug);
      if (!isSlugAvailable) {
        toast({
          title: "Organization URL already exists",
          description: "Please choose a different organization name or modify the URL slug.",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      // Create organization using the service role function to bypass RLS
      const { data, error } = await supabase.rpc('create_organization_with_owner', {
        p_name: formData.name,
        p_slug: formData.slug,
        p_tier: formData.tier,
        p_subdomain: formData.subdomain,
        p_user_id: user.id,
        p_max_seats: getDefaultSeats(formData.tier)
      });

      if (error) {
        console.error('Organization creation error:', error);
        
        // Handle specific constraint violations
        if (error.message?.includes('organizations_slug_key')) {
          toast({
            title: "Organization URL already exists",
            description: "This URL slug is already taken. Please choose a different one.",
            variant: "destructive",
          });
        } else if (error.message?.includes('organizations_subdomain_key')) {
          toast({
            title: "Subdomain already exists",
            description: "This subdomain is already taken. Please choose a different one.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Error",
            description: error.message || "Failed to create organization",
            variant: "destructive",
          });
        }
        setIsLoading(false);
        return;
      }

      console.log('Organization created successfully:', data);

      toast({
        title: "Organization Created",
        description: "Your organization has been successfully created!",
      });

      navigate("/organization/dashboard");
    } catch (error: any) {
      console.error("Error creating organization:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to create organization",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Create Your Organization</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Organization Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Acme Consulting"
                required
              />
            </div>

            <div>
              <Label htmlFor="slug">URL Slug</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                placeholder="acme-consulting"
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                This will be your organization's unique identifier
              </p>
            </div>

            <div>
              <Label htmlFor="subdomain">Subdomain</Label>
              <div className="flex items-center space-x-2">
                <Input
                  id="subdomain"
                  value={formData.subdomain}
                  onChange={(e) => setFormData(prev => ({ ...prev, subdomain: e.target.value }))}
                  placeholder="acme"
                  required
                />
                <span className="text-sm text-gray-500">.csoagent.com</span>
              </div>
            </div>

            <div>
              <Label htmlFor="tier">Plan Tier</Label>
              <Select
                value={formData.tier}
                onValueChange={(value: "professional" | "enterprise") =>
                  setFormData(prev => ({ ...prev, tier: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="professional">Team (5 seats)</SelectItem>
                  <SelectItem value="enterprise">Enterprise (100 seats)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "Creating..." : "Create Organization"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
